/**
 * Readable names for clicked buttons, without recording any text.
 *
 * PostHog masks all text and element attributes (see posthogConfig.ts), so a recorded click only
 * says "a div was clicked". Just before PostHog sees a click, this labels the clicked control with
 * a name taken from its id, a descriptive CSS class, its icon, or the page a link goes to. PostHog
 * then records it as the `button` property. Text, aria-labels and other attributes are never used.
 */

export const BUTTON_ATTRIBUTE = 'data-ph-capture-attribute-button';

const INTERACTIVE =
  'button, a[href], [role="button"], [role="menuitem"], [role="tab"], [role="switch"], [role="checkbox"], ' +
  '[role="radio"], [role="option"], input, select, textarea, summary, .clickable, .cursor-pointer';
const DESCRIPTIVE_CLASS =
  /^[a-z][a-z0-9]*(?:[-_]+[a-z0-9]+)*[-_]+(?:button|btn|item|card|tab|link|menu|trigger|toggle|switch|icon|option|pill|chip|header|input|select|checkbox)$/;
// Layout utility classes (Tailwind) look descriptive but name nothing.
const UTILITY_CLASS =
  /^(?:flex|grid|place|self|justify|content|items|gap|space|divide|text|bg|border|rounded|shadow|ring)-/;
const ICON_CLASS = /^lucide-([a-z0-9-]+)$/;
// Generated ids (database ids, React ids) say nothing about the button and are skipped.
const GENERATED = /[0-9a-f]{8,}|\d{3,}|^radix-|^:r/i;

/** The parts of a DOM element this module reads; a real Element satisfies it. */
export interface ElementLike {
  tagName: string;
  id?: string;
  getAttribute(name: string): string | null;
  closest(selector: string): ElementLike | null;
  querySelector(selector: string): ElementLike | null;
}

const classes = (el: ElementLike | null) => (el?.getAttribute('class') ?? '').split(/\s+/).filter(Boolean);

/** A link's target as a page pattern, with ids replaced: /job/65f…/statistics -> /job/:id/statistics. */
export const linkTarget = (href: string, origin: string): string | null => {
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin) return 'external link';
    return url.pathname
      .split('/')
      .map((part) => (GENERATED.test(part) || /^\d+$/.test(part) ? ':id' : part))
      .join('/');
  } catch {
    return null;
  }
};

/** The control that was clicked, and a name for it. */
export const labelClick = (target: ElementLike, origin: string): { el: ElementLike; label: string } => {
  const el = target.closest(INTERACTIVE) ?? target;
  const named = (name: string) => ({ el, label: name.slice(0, 60) });

  const ids = [el.id, el.querySelector('svg[id]')?.id, target.closest('svg')?.id];
  const id = ids.find((x) => x && !GENERATED.test(x));
  if (id) return named(id);

  const descriptive = classes(el).find((c) => DESCRIPTIVE_CLASS.test(c) && !UTILITY_CLASS.test(c));
  if (descriptive) return named(descriptive);

  const iconEl = target.closest('svg') ?? el.querySelector('svg');
  const icon = [...classes(el), ...classes(iconEl)].map((c) => ICON_CLASS.exec(c)?.[1]).find(Boolean);
  if (icon) return named(`icon: ${icon}`);

  const href = el.tagName.toLowerCase() === 'a' ? el.getAttribute('href') : null;
  const link = href ? linkTarget(href, origin) : null;
  if (link) return named(`link: ${link}`);

  const tag = el.tagName.toLowerCase();
  const kind = el.getAttribute('role') ?? (tag === 'input' ? el.getAttribute('type') : null);
  return named(kind ? `${tag} (${kind})` : tag);
};

/** Labels every click before PostHog's own listener (on document) records it. */
export const installClickLabels = () => {
  window.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target as unknown as ElementLike | null;
        if (!target || typeof target.closest !== 'function') return;
        const { el, label } = labelClick(target, window.location.origin);
        const node = el as unknown as Element;
        if (!node.hasAttribute(BUTTON_ATTRIBUTE)) node.setAttribute(BUTTON_ATTRIBUTE, label);
      } catch {
        // Analytics must never break a click.
      }
    },
    { capture: true },
  );
};
